import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useT } from "@/i18n/I18nContext";
import { fmt } from "@/lib/api";
import {
  ResponsiveContainer, BarChart, Bar, AreaChart, Area, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, Cell,
} from "recharts";

const CHART_COLORS = ["#0E7490", "#14B8A6", "#F59E0B", "#3B82F6", "#22C55E", "#A855F7"];
// Static recharts style/config objects — module scope so they aren't re-created each render.
const TOOLTIP_STYLE = { fontSize: 12, borderRadius: 8, border: "1px solid hsl(var(--border))" };
const BAR_CURSOR = { fill: "hsl(var(--secondary))" };
const CHART_MARGIN = { left: -18, right: 8, top: 6 };
const CHART_MARGIN_WIDE = { left: 4, right: 8, top: 6 };
const LEGEND_STYLE = { fontSize: 12 };

/** Trend charts for the dashboard: milk-in vs produced, volume by product, and by-products per month. */
export const DashboardCharts = ({ trends }) => {
  const { t } = useT();
  return (
    <>
      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">{t("dashboard.milkInVsProduced")} — {trends?.month_label || t("dashboard.thisMonth")}</CardTitle>
          </CardHeader>
          <CardContent>
            {trends ? (
              <ResponsiveContainer width="100%" height={240}>
                <AreaChart data={trends.timeline} margin={CHART_MARGIN}>
                  <defs>
                    <linearGradient id="gIntake" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.5} />
                      <stop offset="95%" stopColor="#3B82F6" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="gProd" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#14B8A6" stopOpacity={0.5} />
                      <stop offset="95%" stopColor="#14B8A6" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                  <XAxis dataKey="date" fontSize={11} tickLine={false} axisLine={false} stroke="hsl(var(--muted-foreground))" />
                  <YAxis fontSize={11} tickLine={false} axisLine={false} stroke="hsl(var(--muted-foreground))" />
                  <Tooltip contentStyle={TOOLTIP_STYLE} />
                  <Area type="monotone" dataKey="intake" name={t("dashboard.milkInL")} stroke="#3B82F6" fill="url(#gIntake)" strokeWidth={2} />
                  <Area type="monotone" dataKey="produced" name={t("dashboard.producedL")} stroke="#14B8A6" fill="url(#gProd)" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[240px] animate-pulse rounded-lg bg-secondary" />
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">{t("dashboard.prodVolumeByProduct")}</CardTitle>
          </CardHeader>
          <CardContent>
            {trends && trends.production_by_product.length > 0 ? (
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={trends.production_by_product} margin={CHART_MARGIN}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                  <XAxis dataKey="product" fontSize={11} tickLine={false} axisLine={false} stroke="hsl(var(--muted-foreground))" />
                  <YAxis fontSize={11} tickLine={false} axisLine={false} stroke="hsl(var(--muted-foreground))" />
                  <Tooltip contentStyle={TOOLTIP_STYLE} cursor={BAR_CURSOR} />
                  <Bar dataKey="volume" name={t("dashboard.milkUsedL")} radius={[6, 6, 0, 0]}>
                    {trends.production_by_product.map((entry, i) => (
                      <Cell key={entry.product ?? i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex h-[240px] items-center justify-center text-sm text-muted-foreground">{t("dashboard.noProdData")}</div>
            )}
          </CardContent>
        </Card>
      </div>
    </>
  );
};

/** Monthly loss totals (kg) stacked by write-off reason — admin/manager only.
    Waste patterns jump out: tall amber = expiry problem, tall red = handling damage. */
const REASON_FILL = {
  expired: "#F59E0B",
  damaged: "#DC2626",
  quality: "#3B82F6",
  other: "#94A3B8",
  unspecified: "#94A3B8",
};
const CUSTOM_REASON_FILLS = ["#0E7490", "#A855F7", "#22C55E", "#EC4899", "#8B5CF6", "#14B8A6"];
const BUILTIN_REASONS = ["expired", "damaged", "quality", "other", "unspecified"];

export const LossTrendChart = ({ trend }) => {
  const { t } = useT();
  const fmtMonth = (m) => `${m.slice(5, 7)}/${m.slice(2, 4)}`; // "2026-03" -> "03/26"
  const rows = (trend?.data || []).map((r) => ({ ...r, month: fmtMonth(r.month) }));
  const reasons = trend?.reasons || [];
  const hasData = (trend?.total_kg || 0) > 0;
  const currency = trend?.currency || "RON";
  // Custom tenant reasons display as-is (they are plain labels) and get palette colors.
  const reasonLabel = (r) => (BUILTIN_REASONS.includes(r) ? t(`stockMovements.losses.reason.${r}`) : r);
  const customs = reasons.filter((r) => !REASON_FILL[r]);
  const reasonFill = (r) => REASON_FILL[r] || CUSTOM_REASON_FILLS[customs.indexOf(r) % CUSTOM_REASON_FILLS.length];
  // Tooltip title shows the month's combined kg + estimated money value, so waste "hits home".
  const monthLabel = (label) => {
    const r = rows.find((x) => x.month === label);
    if (!r) return label;
    const val = r.value > 0 ? ` · ~${fmt(r.value, 0)} ${currency}` : "";
    return `${label} — ${fmt(r.total_kg, 1)} kg${val}`;
  };
  return (
    <div className="mt-6">
      <Card data-testid="loss-trend-card">
        <CardHeader>
          <div className="flex items-start justify-between gap-3">
            <div>
              <CardTitle className="text-base">{t("dashboard.lossTrend")}</CardTitle>
              <p className="text-xs text-muted-foreground">{t("dashboard.lossTrendHint")}</p>
            </div>
            {hasData && (trend?.total_value || 0) > 0 && (
              <div className="text-right">
                <div className="font-mono text-lg font-semibold tabular-nums text-[#DC2626]" data-testid="loss-trend-total-value">
                  ~{fmt(trend.total_value, 0)} <span className="text-sm font-normal">{currency}</span>
                </div>
                <div className="text-[11px] text-muted-foreground">{t("dashboard.lossValueLabel")}</div>
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {!trend ? (
            <div className="h-[240px] animate-pulse rounded-lg bg-secondary" />
          ) : hasData ? (
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={rows} margin={CHART_MARGIN}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                <XAxis dataKey="month" fontSize={11} tickLine={false} axisLine={false} stroke="hsl(var(--muted-foreground))" />
                <YAxis fontSize={11} tickLine={false} axisLine={false} stroke="hsl(var(--muted-foreground))"
                  tickFormatter={(v) => fmt(v, 0)} />
                <Tooltip contentStyle={TOOLTIP_STYLE} cursor={BAR_CURSOR}
                  labelFormatter={monthLabel}
                  formatter={(value, name) => [`${fmt(value, 2)} kg`, reasonLabel(name)]} />
                <Legend wrapperStyle={LEGEND_STYLE} formatter={(v) => reasonLabel(v)} />
                {reasons.map((r, i) => (
                  <Bar key={r} dataKey={r} stackId="loss" fill={reasonFill(r)}
                    radius={i === reasons.length - 1 ? [6, 6, 0, 0] : [0, 0, 0, 0]} maxBarSize={48} />
                ))}
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex h-[240px] items-center justify-center text-sm text-muted-foreground" data-testid="loss-trend-empty">
              {t("dashboard.noLossData")}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

/** Monthly average specific consumption (kg net milk / kg product) per product — admin/manager only.
    Trends jump out: rising lines mean batches need more milk per kg of product. */
export const ConsumptionTrendChart = ({ trend }) => {
  const { t, td } = useT();
  const fmtMonth = (m) => `${m.slice(5, 7)}/${m.slice(2, 4)}`; // "2026-03" -> "03/26"
  const rows = (trend?.months || []).map((m) => {
    const row = { month: fmtMonth(m) };
    (trend?.series || []).forEach((s) => {
      const pt = s.data.find((d) => d.month === m);
      row[s.product] = pt?.avg_sc ?? null;
      // Constant dashed target line per product — makes the gap to target stand out.
      if (s.target != null) row[`${s.product}__target`] = s.target;
    });
    return row;
  });
  const hasData = (trend?.series || []).length > 0;
  return (
    <div className="mt-6">
      <Card data-testid="consumption-trend-card">
        <CardHeader>
          <CardTitle className="text-base">{t("dashboard.scTrend")}</CardTitle>
          <p className="text-xs text-muted-foreground">{t("dashboard.scTrendHint")}</p>
        </CardHeader>
        <CardContent>
          {!trend ? (
            <div className="h-[260px] animate-pulse rounded-lg bg-secondary" />
          ) : hasData ? (
            <ResponsiveContainer width="100%" height={260}>
              <LineChart data={rows} margin={CHART_MARGIN}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                <XAxis dataKey="month" fontSize={11} tickLine={false} axisLine={false} stroke="hsl(var(--muted-foreground))" />
                <YAxis fontSize={11} tickLine={false} axisLine={false} stroke="hsl(var(--muted-foreground))"
                  domain={["auto", "auto"]} tickFormatter={(v) => fmt(v, 1)} />
                <Tooltip contentStyle={TOOLTIP_STYLE}
                  formatter={(value, name) => [
                    `${fmt(value, 2)} ${t("dashboard.scUnitShort")}`,
                    name.endsWith("__target") ? `${td(name.slice(0, -8))} — ${t("dashboard.scTargetLabel")}` : td(name),
                  ]} />
                <Legend wrapperStyle={LEGEND_STYLE} formatter={(v) => td(v)} />
                {trend.series.map((s, i) => (
                  <Line key={s.product} type="monotone" dataKey={s.product} connectNulls
                    stroke={CHART_COLORS[i % CHART_COLORS.length]} strokeWidth={2}
                    dot={{ r: 3 }} activeDot={{ r: 5 }} />
                ))}
                {trend.series.filter((s) => s.target != null).map((s, i) => (
                  <Line key={`${s.product}__target`} dataKey={`${s.product}__target`}
                    stroke={CHART_COLORS[trend.series.indexOf(s) % CHART_COLORS.length]}
                    strokeWidth={1.5} strokeDasharray="6 4" strokeOpacity={0.6}
                    dot={false} activeDot={false} legendType="none" isAnimationActive={false} />
                ))}
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex h-[260px] items-center justify-center text-sm text-muted-foreground" data-testid="consumption-trend-empty">
              {t("dashboard.noScData")}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
